'use client';

import { ColumnDef, FilterFn } from '@tanstack/react-table';
import { CampaignStatus, CampaignType, CampaignChannel } from './types';
import { Campaign } from './hooks/use-campaigns';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, MessageSquare, Bell, Mail, Percent, Megaphone } from 'lucide-react';
import { Badge, BadgeVariant } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { format } from 'date-fns';
import { LucideIcon } from 'lucide-react';
import { CampaignScheduling, UniversalTimeConfig, LocalTimeConfig } from './types';
import { formatInTimeZone } from 'date-fns-tz';
import { formatTimeDisplay } from './utils/scheduling';
import { useState } from 'react';
import { DeleteCampaignDialog } from './components/delete-campaign-dialog';
import { PauseCampaignDialog } from './components/pause-campaign-dialog';
import { ResumeCampaignDialog } from './components/resume-campaign-dialog';
import { getTimeZoneDisplay } from '@/utils/format-utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const CampaignTypeIcons = {
  [CampaignType.BLAST]: Megaphone,
  [CampaignType.SPLIT_SEND]: Percent,
  [CampaignType.UNSPECIFIED]: Megaphone
} as const;

const CampaignTypeLabels = {
  [CampaignType.BLAST]: 'Blast',
  [CampaignType.SPLIT_SEND]: 'Split Send',
  [CampaignType.UNSPECIFIED]: 'Draft'
} as const;

const ChannelIcons: Record<CampaignChannel, LucideIcon> = {
  sms: MessageSquare,
  email: Mail,
  push: Bell
} as const;

const ChannelLabels: Record<CampaignChannel, string> = {
  sms: 'SMS Message',
  email: 'Email',
  push: 'Push Notification'
} as const;

const StatusBadgeVariants: Record<CampaignStatus, BadgeVariant> = {
  draft: 'status-draft',
  scheduled: 'status-scheduled',
  sending: 'status-sending',
  paused: 'status-paused',
  delivered: 'status-delivered',
  suspended: 'status-suspended'
};

const channelsFilterFn: FilterFn<Campaign> = (row, columnId, filterValue: CampaignChannel[]) => {
  if (!filterValue.length) return true;
  const rowChannels = row.getValue<CampaignChannel[]>('channels');
  return filterValue.some((channel) => rowChannels.includes(channel));
};

interface CampaignActionsProps {
  row: Campaign;
  status: CampaignStatus;
}

function CampaignActions({ row, status }: CampaignActionsProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [showResumeDialog, setShowResumeDialog] = useState(false);

  // Normalize status to lowercase for consistent comparisons
  const normalizedStatus = status.toLowerCase();

  // Determine if campaign is editable based on status
  const isEditable = ['draft', 'scheduled'].includes(normalizedStatus);

  // Define menu items with their conditional visibility
  const menuItems = [
    {
      label: isEditable ? 'Edit' : 'View Campaign',
      show: true // Always show since all campaigns can be viewed
    },
    {
      label: 'Duplicate',
      onClick: (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        // TODO: Implement duplicate functionality
      },
      show: true
    },
    {
      label: 'Resume',
      onClick: (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        setShowResumeDialog(true);
      },
      show: normalizedStatus === 'paused'
    },
    {
      label: 'Pause',
      onClick: (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        setShowPauseDialog(true);
      },
      show: normalizedStatus === 'sending'
    },
    {
      label: 'Delete',
      onClick: (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        setShowDeleteDialog(true);
      },
      show: ['draft', 'scheduled', 'paused'].includes(normalizedStatus),
      className: 'text-destructive-foreground focus:text-destructive-foreground'
    }
  ];

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-card/50">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {menuItems.map(
            (item, index) =>
              item.show && (
                <DropdownMenuItem key={index} className={item.className} onClick={item.onClick}>
                  {item.label}
                </DropdownMenuItem>
              )
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteCampaignDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        campaignId={row.campaignId}
        campaignName={row.name}
        merchantId={row.merchantId}
      />

      <PauseCampaignDialog
        open={showPauseDialog}
        onOpenChange={setShowPauseDialog}
        campaignId={row.campaignId}
        campaignName={row.name}
      />

      <ResumeCampaignDialog
        open={showResumeDialog}
        onOpenChange={setShowResumeDialog}
        campaignId={row.campaignId}
        campaignName={row.name}
      />
    </>
  );
}

export const columns: ColumnDef<Campaign>[] = [
  {
    accessorKey: 'name',
    enableResizing: true,
    size: 250,
    minSize: 220,
    header: 'Campaign Name',
    enableSorting: true,
    cell: ({ row }) => (
      <div className="font-medium text-foreground truncate">
        <span className="border-b-2 border-dotted pb-1 inline-block truncate max-w-full">
          {row.getValue('name')}
        </span>
      </div>
    )
  },
  {
    accessorKey: 'type',
    enableResizing: true,
    size: 100,
    minSize: 90,
    header: 'Type',
    enableSorting: true,
    cell: ({ row }) => {
      const type = row.getValue<CampaignType>('type');
      const Icon = CampaignTypeIcons[type];
      return (
        <TooltipProvider key={type}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Icon className="h-4 w-4 text-foreground opacity-80" />
            </TooltipTrigger>
            <TooltipContent>
              <p>{CampaignTypeLabels[type]}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
  },
  {
    accessorKey: 'channels',
    enableResizing: true,
    enableColumnFilter: true,
    filterFn: channelsFilterFn,
    size: 120,
    minSize: 110,
    header: 'Channel',
    enableSorting: true,
    cell: ({ row }) => {
      const channels = row.getValue<CampaignChannel[]>('channels');
      return (
        <div className="flex items-center gap-2">
          {channels.map((channel) => {
            const Icon = ChannelIcons[channel.toLowerCase() as CampaignChannel];
            return (
              <TooltipProvider key={channel}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Icon className="h-4 w-4 text-foreground opacity-80" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{ChannelLabels[channel.toLowerCase() as CampaignChannel]}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>
      );
    }
  },
  {
    accessorKey: 'createdAt',
    enableResizing: true,
    size: 160,
    minSize: 170,
    header: 'Created',
    enableSorting: true,
    cell: ({ row }) => {
      const date = new Date(row.getValue<string>('createdAt'));
      const scheduled = row.original.scheduled as CampaignScheduling | undefined;
      const tzDisplay = getTimeZoneDisplay(scheduled);

      return (
        <div className="flex flex-col truncate">
          <span className="font-medium truncate">{format(date, 'MM-dd-yyyy')}</span>
          <span className="text-sm text-muted-foreground truncate">
            {format(date, 'h:mm a')} {tzDisplay}
          </span>
        </div>
      );
    }
  },
  {
    accessorKey: 'scheduled',
    enableResizing: true,
    size: 160,
    minSize: 170,
    header: 'Scheduled',
    enableSorting: true,
    cell: ({ row }) => {
      const scheduled = row.getValue<CampaignScheduling | undefined>('scheduled');
      // Handle cases where scheduled is null/undefined or type is 'draft'
      if (!scheduled || scheduled.type === 'draft') {
        return '-';
      }

      // Handle 'now' case
      if (scheduled.type === 'now') {
        return 'Now';
      }

      if (!scheduled.config) return '-';

      const tzDisplay = getTimeZoneDisplay(scheduled);

      if (scheduled.config.type === 'universal') {
        const universalConfig = scheduled.config.config as UniversalTimeConfig;
        if (!universalConfig.date || !universalConfig.time) return '-';

        const dateStr = `${universalConfig.date} ${universalConfig.time}`;
        return (
          <div className="flex flex-col truncate">
            <span className="font-medium truncate">
              {formatInTimeZone(dateStr, universalConfig.timezone, 'yyyy-MM-dd')}
            </span>
            <span className="text-sm text-muted-foreground truncate">
              {formatInTimeZone(dateStr, universalConfig.timezone, 'h:mm a')} {tzDisplay}
            </span>
          </div>
        );
      }

      // Local time
      const localConfig = scheduled.config.config as LocalTimeConfig;
      if (!localConfig.date || !localConfig.time) return '-';

      return (
        <div className="flex flex-col truncate">
          <span className="font-medium truncate">
            {format(new Date(localConfig.date), 'yyyy-MM-dd')}
          </span>
          <span className="text-sm text-muted-foreground truncate">
            {formatTimeDisplay(localConfig.time)} {tzDisplay}
          </span>
        </div>
      );
    }
  },
  {
    accessorKey: 'status',
    enableResizing: true,
    size: 120,
    minSize: 130,
    header: 'Status',
    enableSorting: true,
    cell: ({ row }) => {
      const status = row.getValue<CampaignStatus>('status');
      return (
        <Badge
          variant={StatusBadgeVariants[status]}
          className="inline-flex items-center justify-center px-3 py-1 capitalize group-hover:!bg-opacity-100"
        >
          {status}
        </Badge>
      );
    }
  },
  {
    accessorKey: 'metrics.opened',
    enableResizing: true,
    size: 120,
    minSize: 120,
    header: 'Opened',
    enableSorting: true,
    cell: ({ row }) => {
      const opened = row.original.metrics.opened;
      return (
        <div className="flex flex-col truncate">
          <span className="font-semibold tabular-nums text-foreground truncate">
            {opened.toLocaleString()}
          </span>
          {opened && (
            <span className="flex items-center gap-0.5 text-xs text-success-foreground truncate">
              <span>↑</span>
              <span className="tabular-nums">23%</span>
            </span>
          )}
        </div>
      );
    }
  },
  {
    accessorKey: 'metrics.sent',
    enableResizing: true,
    size: 120,
    minSize: 120,
    header: 'Sent',
    enableSorting: true,
    cell: ({ row }) => {
      const sent = row.original.metrics.sent;
      return (
        <div className="flex flex-col truncate">
          <span className="font-semibold tabular-nums text-foreground truncate">
            {sent.toLocaleString()}
          </span>
          <span className="flex items-center gap-0.5 text-xs text-success-foreground truncate">
            <span>↑</span>
            <span className="tabular-nums">41</span>
          </span>
        </div>
      );
    }
  },
  {
    accessorKey: 'metrics.clicked',
    enableResizing: true,
    size: 120,
    minSize: 120,
    header: 'Clicked',
    enableSorting: true,
    cell: ({ row }) => {
      const clicked = row.original.metrics.clicked;
      return (
        <div className="flex flex-col truncate">
          <span className="font-semibold tabular-nums text-foreground truncate">
            {clicked.toLocaleString()}
          </span>
          <span className="text-xs text-muted-foreground tabular-nums truncate">12% CTR</span>
        </div>
      );
    }
  },
  {
    accessorKey: 'metrics.ordered',
    enableResizing: true,
    size: 120,
    minSize: 120,
    header: 'Ordered',
    enableSorting: true,
    cell: ({ row }) => {
      const ordered = row.original.metrics.ordered;
      return (
        <div className="flex flex-col truncate">
          <span className="font-semibold tabular-nums text-foreground truncate">
            {ordered.toLocaleString()}
          </span>
          <span className="text-xs text-muted-foreground tabular-nums truncate">2.32% CVR</span>
        </div>
      );
    }
  },
  {
    accessorKey: 'metrics.earned',
    enableResizing: true,
    size: 140,
    minSize: 140,
    header: 'Earned',
    enableSorting: true,
    cell: ({ row }) => {
      const earned = row.original.metrics.earned;
      return (
        <div className="flex flex-col truncate">
          <span className="font-semibold tabular-nums text-foreground truncate">
            {earned
              ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                  earned
                )
              : '-'}
          </span>
          <span className="text-xs text-muted-foreground tabular-nums truncate">$20.83 EPM</span>
        </div>
      );
    }
  },
  {
    id: 'actions',
    enableResizing: true,
    size: 50,
    minSize: 50,
    cell: ({ row }) => {
      const status = row.getValue<CampaignStatus>('status');
      return <CampaignActions row={row.original} status={status} />;
    },
    meta: {
      className: 'sticky right-0 bg-card group-hover:bg-accent/20 transition-colors duration-200'
    }
  }
];
